import * as React from "react"
import { createTheme, Theme } from "@mui/material/styles"
// import createTypography, {
//   TypographyOptions
// } from "@mui/material/styles/createTypography"
// import Overrides from "@mui/material/overrides"

import chroma, { Color, Scale } from "chroma-js"
import styled from 'styled-components'

import { fontFacesDefault, typographyDefault } from "./styles/TypographyStyles"

import { filters } from "./styles/svg-utils"
import { PaletteMode, DeprecatedThemeOptions, DialogProps, Paper, PaperProps } from "@mui/material"

import { meshBgImageStyles, meshBgTransImageUri } from "./styles/BackgroundStyles"


interface TColorScales {
  [key: string]: Scale<Color>
  bg: Scale<Color>
  viz: Scale<Color>
  fg: Scale<Color>
  paper: Scale<Color>
  light: Scale<Color>
  primary: Scale<Color>
  secondary: Scale<Color>
  spice: Scale<Color>
  blues: Scale<Color>
  purples: Scale<Color>
  pinks: Scale<Color>
  reds: Scale<Color>
  greens: Scale<Color>
  importance: Scale<Color>
  importance2: Scale<Color>
  brewer1: Scale<Color>
  temperature: Scale<Color>
  warning: Scale<Color>
  warningInverted: Scale<Color>
  financial: Scale<Color>
  financialBlack: Scale<Color>
  financialAlpha: Scale<Color>
}

type TBasePalette = {
  bg: string
  bgContent: string
  fg: string
  fgAlpha: string
  border: string
}

type TBaseColors = {
  [key: string]: TBasePalette
  dark: TBasePalette
  light: TBasePalette
}

type TReactVizOverrides = {
  globalCSS: string
  themeName: string
  XYPlot: {
    margin: number
  }
  XAxis: {
    [key: string]: any
  }
  YAxis: {
    [key: string]: any
  }
  MarkSeries: {
    [key: string]: any
  }
  MarkSeriesStyle: {
    [key: string]: any
  }
  SVG: {
    [key: string]: any
  }
}

type TReactVizThemes = {
  light: TReactVizOverrides
  dark: TReactVizOverrides
  fabriq: TReactVizOverrides
}

type GammaUIThemeOverrides = {
  [key: string]: any
}

type TCategoryColor = { light: string; main: string; dark: string }
type TCategoryColors = TCategoryColor[]

export interface GammaUITheme {
  basePalette: TBasePalette
  colorScales: TColorScales
  categoryColors: TCategoryColors
  series: string[]
  seriesBCG: string[]
  seriesPaletteKey: "light" | "main" | "dark"
  themeName: string
  baseTheme: "light" | "dark"
  viz: TReactVizOverrides
  // overrides: Overrides & GammaUIThemeOverrides
}

export interface GammaUIThemeOptions {
  basePalette: TBasePalette
  categoryColors: TCategoryColors
  colorScales: TColorScales
  series: string[]
  seriesBCG: string[]
  seriesQ: string[]
  seriesPaletteKey: "light" | "main" | "dark"
  themeName: string
  baseTheme: "light" | "dark"
  viz: TReactVizOverrides
  // overrides: Overrides & GammaUIThemeOverrides
}

interface ISVGPatternProps {
  id: string
  fill?: string
  textureOpacity?: number
  fillOpacity?: number
}

// patch MUI's type definitions with our additions
declare module "@mui/material/styles/createTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface Theme extends GammaUITheme {}

  // allow configuration using `createTheme`
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DeprecatedThemeOptions extends GammaUIThemeOptions {}
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
// interface ExtendedTheme extends GammaUITheme {}
// interface ExtendedThemeOptions extends GammaUIThemeOptions {}

const MeshPaperWrap = styled.div`
  padding: 0.5rem;
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  background: black;

  .MuiPaper-root {
    z-index: 2;
  }
`

const MeshPaperBorderImg = styled.aside`
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 1;
  left: 0;
  top: 0;
  opacity: 0.8;
  border: 1px solid white;
  border-radius: 6px;
`

const MeshPaper = (props: PaperProps) => (
  <MeshPaperWrap>
    <MeshPaperBorderImg style={{ ...meshBgImageStyles }} />
    <Paper {...props} />
  </MeshPaperWrap>
)

const baseColors: TBaseColors = {
  dark: {
    bg: "rgb(15, 17, 22)",
    bgContent: "rgb(15, 17, 22)",
    fg: "rgb(251, 252, 254)",
    fgAlpha: "rgba(251, 252, 254, 0.5)",
    border: "rgb(44, 44, 44)"
  },
  light: {
    bg: "rgb(251, 252, 254)",
    bgContent: "rgb(245, 247, 250)",
    fg: "rgb(11, 11, 11)",
    fgAlpha: "rgba(11, 11, 11, 0.5)",
    border: "rgb(232, 232, 232)"
  },
  fabriq: {
    bg: "rgb(251, 252, 254)",
    bgContent: "rgb(245, 245, 245)",
    fg: "rgb(11, 11, 11)",
    fgAlpha: "rgba(11, 11, 11, 0.5)",
    border: "rgb(232, 232, 232)"
  }
}

// const defaultChartSeries = [
//   "#A5A8AE",
//   "#46DD81",
//   "#DCE68C",
//   "#8CD2DC",
//   "#3C78BE",
//   "#E6B4B4",
//   "#BC5B99",
//   "#28804A"
// ]

const longChartSeries = [
  "#00d870",
  "#146145",
  "#5188c8",
  "#c7c7c7",
  "#45cee3",
  "#875484",
  "#ea5353",
  "#bf6363",
  "#1a94a0",
  "#94a0b7",
  "#becc72",
  "#3709d1"
]

const bcgPalette = [
  "#E3E2DD",
  "#30C0A4",
  "#4C5460",
  "#A5A15F",
  "#6A93C4",
  "#E41C54",
  "#106840",
  "#3DAB93",
  "#D3D539",
  "#A1D1E7",
  "#640C34",
  "#555F90",
  "#ECE79D",
  "#7B7574",
  "#995C54",
  "#E6BC87",
  "#D49B80",
  "#B8C060",
  "#47785E",
  "#346444"
]

const categoryColorsBase = [
  "#00d870",
  "#5188c8",
  "#ea5353",
  "#875484",
  "#1a94a0",
  "#becc72",
  "#E41C54",
  "#3709d1",
  "#106840",
  "#3DAB93",
  "#D3D539",
  "#640C34",
  "#555F90",
  "#ECE79D",
  "#995C54",
  "#E6BC87",
  "#94a0b7"
]

// const categoryColors = categoryColorsBase.map(c => ({
//   light: chroma(c).brighten(2.5).css(),
//   main: chroma(c).brighten(2).css(),
//   dark: chroma(c).brighten(1.2).css()
// }))

const categoryColors: TCategoryColors = categoryColorsBase.map(c => ({
  light: chroma.mix(c, 'white', 0.75, 'rgb').brighten(0.5).css(),
  main: chroma.mix(c, 'white', 0.5, 'rgb').brighten(1).css(),
  dark: chroma.mix(c, 'white', 0.25, 'rgb').brighten(0.2).css()
}))



// const longChartSeries = [
//   "hsl(151, 98%, 50%)",
//   "hsl(158, 66%, 23%)",
//   "hsl(67, 64%, 73%)",
//   "hsl(212, 52%, 55%)",
//   "hsl(165, 47%, 40%)",
//   "hsl(188, 53%, 70%)",
//   "hsl(0, 0%, 78%)",
//   "hsl(188, 74%, 58%)",
//   "hsl(0, 0%, 41%)",
//   "hsl(304, 23%, 43%)",
//   "hsl(0, 0%, 59%)",
//   "hsl(333, 87%, 73%)",
//   "hsl(0, 78%, 62%)",
//   "hsl(0, 42%, 57%)",
//   "hsl(30, 87%, 66%)"
// ]

// https://material.io/resources/color/#!/?view.left=0&view.right=1&primary.color=43fda2&secondary.color=4B3A40
// https://react-theming.github.io/create-mui-theme/

interface IGenerateCustomColorScalesProps {
  colorScales: {
    [key: string]: string[]
  }
  mode: PaletteMode
}

export function generateCustomColorScales({
  colorScales,
  mode
}: IGenerateCustomColorScalesProps) {
  const baseScales = getColorScales(mode)
  const chromaScales: { [key: string]: Scale<Color> } = {}

  for (const key in colorScales) {
    if (typeof key === "string") {
      chromaScales[key] = chroma.scale(colorScales[key])
    }
  }

  return {
    ...baseScales,
    ...chromaScales
  }
}

const commonColorScales = {
  light: chroma.scale(["rgb(200,200,200)", "rgb(255,355,355)"]),
  primary: chroma.scale(["hsl(158, 66%, 24%)", "hsl(143, 69%, 57%)"]),
  secondary: chroma.scale(["hsl(203, 51%, 24%)", "hsl(188, 68%, 68%)"]),
  spice: chroma.scale(["hsl(330, 54%, 24%)", "hsl(330, 100%, 68%)"]),
  blues: chroma.scale(["hsl(203, 51%, 24%)", "hsl(188, 68%, 68%)"]),
  purples: chroma.scale(["hsl(263, 51%, 24%)", "hsl(263, 68%, 68%)"]),
  pinks: chroma.scale(["hsl(291, 100%, 24%)", "hsl(291, 100%, 50%)"]),
  reds: chroma.scale(["hsl(0, 92%, 24%)", "hsl(0, 92%, 46%)"]),
  greens: chroma.scale(["hsl(158, 66%, 24%)", "hsl(143, 69%, 57%)"]),
  importance: chroma.scale([
    "rgb(61, 58, 64)",
    "rgb(41,94,126)",
    "rgb(48,193,215)",
    "hsl(143, 69%, 57%)"
  ]),
  importance2: chroma.scale([
    "hsl(270, 45%, 25%)",
    "hsl(203, 51%, 33%)",
    "hsl(188, 68%, 52%)",
    "hsl(143, 69%, 57%)"
  ]),
  brewer1: chroma.scale("RdBu"),
  temperature: chroma.scale([
    "hsl(188, 68%, 58%)",
    "hsl(156, 33%, 54%)",
    "hsl(61, 32%, 50%)",
    "hsl(34, 63%, 54%)",
    "hsl(16, 77%, 58%)",
    "hsl(357, 97%, 62%)"
  ]),
  warning: chroma.scale([
    "hsl(143, 69%, 57%)",
    "hsl(60, 100%, 48%)",
    "hsl(343, 81%, 31%)"
  ]),
  warningInverted: chroma.scale([
    "hsl(343, 81%, 31%)",
    "hsl(60, 100%, 48%)",
    "hsl(143, 69%, 57%)"
  ]),
  financial: chroma.scale([
    "hsl(343, 81%, 41%)",
    "hsl(100, 100%, 98%)",
    "hsl(143, 69%, 57%)"
  ]),
  financialBlack: chroma.scale([
    "hsl(343, 81%, 41%)",
    "hsl(0, 0%, 8%)",
    "hsl(143, 69%, 57%)"
  ]),
  financialAlpha: chroma.scale([
    "hsla(343, 81%, 41%, 1.0)",
    "hsla(100, 100%, 98%, 0.1)",
    "hsla(143, 69%, 57%, 1.0)"
  ])
}

interface TAllColorScales {
  [key: string]: TColorScales
  dark: TColorScales
  light: TColorScales
  fabriq: TColorScales
}

const getColorScales = (key: string) =>
  key in allColorScales ? allColorScales[key] : allColorScales.dark

export const allColorScales: TAllColorScales = {
  dark: {
    ...commonColorScales,
    bg: chroma.scale(["rgb(15, 17, 22)", "rgb(41, 47, 61)"]),
    viz: chroma.scale(["rgb(15, 17, 22)", "rgb(41, 47, 61)"]),
    fg: chroma.scale(["rgb(255, 255, 255)", "rgb(15, 17, 22)"]),
    paper: chroma.scale(["rgb(255, 255, 255)", "rgb(15, 17, 22)"])
  },
  light: {
    ...commonColorScales,
    bg: chroma.scale(["rgb(252, 252, 252)", "rgb(255, 255, 255)"]),
    viz: chroma.scale(["rgb(255, 255, 255)", "rgb(155, 155, 155)"]),
    fg: chroma.scale(["rgb(0, 0, 0)", "rgb(255, 255, 255)"]),
    paper: chroma.scale(["rgb(255, 255, 255)", "rgb(255, 255, 255)"])
  },
  fabriq: {
    ...commonColorScales,
    bg: chroma.scale(["rgb(252, 252, 252)", "rgb(255, 255, 255)"]),
    viz: chroma.scale(["rgb(255, 255, 255)", "rgb(155, 155, 155)"]),
    fg: chroma.scale(["rgb(0, 0, 0)", "rgb(255, 255, 255)"]),
    paper: chroma.scale(["rgb(255, 255, 255)", "rgb(255, 255, 255)"])
  }
}

export function generateVizTheme({
  base,
  colorScales,
  themeName
}: {
  base: TBasePalette
  colorScales: TColorScales
  themeName: string
}): TReactVizOverrides {
  return {
    globalCSS: `
    .rv-treemap {
      font-size: 12px;
      position: relative;
    }

    .rv-treemap__leaf {
      overflow: hidden;
      position: absolute;
    }

    .rv-treemap__leaf--circle {
      align-items: center;
      border-radius: 100%;
      display: flex;
      justify-content: center;
    }

    .rv-treemap__leaf__content {
      overflow: hidden;
      padding: 10px;
      text-overflow: ellipsis;
    }

    .rv-xy-plot {
      color: ${colorScales.fg(0.76).css()};
      position: relative;
    }

    .rv-xy-plot canvas {
      pointer-events: none;
    }

    .rv-xy-plot .rv-xy-canvas {
      pointer-events: none;
      position: absolute;
    }

    .rv-xy-plot__inner {
      display: block;
    }

    .rv-xy-plot__axis__line {
      fill: none;
      stroke-width: 2px;
      stroke: ${colorScales.fg(0.1).css()};
    }

    .rv-xy-plot__axis__tick__line {
      stroke: ${colorScales.fg(0.1).css()};
    }

    .rv-xy-plot__axis__tick__text {
      fill: ${colorScales.fg(0.42).css()};
      font-size: 11px;
    }

    .rv-xy-plot__axis__title text {
      fill: ${base.bg};
      font-size: 16px;
    }

    .rv-xy-plot__axis--vertical .rv-xy-plot__axis__title {
      transform: translateX(0px);
    }

    .rv-xy-plot__axis--horizontal .rv-xy-plot__axis__title {
      transform: translateY(50px);
    }

    .rv-xy-plot__grid-lines__line {
      stroke: ${colorScales.fg(0.8).alpha(0.8).css()} !important;
      stroke-dasharray: 1,2;
    }

    .rv-xy-plot__circular-grid-lines__line {
      fill-opacity: 0;
      stroke: ${colorScales.viz(0.1).css()};
    }

    .rv-xy-plot__series,
    .rv-xy-plot__series path {
      pointer-events: all;
    }

    .rv-xy-plot__circular-grid-lines__line {
      fill-opacity: 0;
      stroke: ${colorScales.fg(0.1).css()};
    }

    .rv-xy-plot__series,
    .rv-xy-plot__series path {
      pointer-events: all;
    }

    .rv-xy-plot__series--line {
      fill: none;
      stroke: ${base.fg};
      stroke-width: 2px;
    }

    .rv-crosshair {
      position: absolute;
      font-size: 11px;
      pointer-events: none;
    }

    .rv-crosshair__line {
      background: none !important;
      width: 1px;
      border-left: 1px dashed rgb(71, 211, 217);
    }

    .rv-crosshair__inner {
      position: absolute;
      text-align: left;
      top: 0;
      background: none;
    }

    .rv-crosshair__inner__content {
      border-radius: 4px;
      background: ${colorScales.fg(0.22).css()};
      color: ${base.bg};
      font-size: 12px;
      padding: 7px 10px;
      box-shadow: 0 2px 4px ${base.fgAlpha};
    }

    .rv-crosshair__inner--left {
      right: 4px;
    }

    .rv-crosshair__inner--right {
      left: 4px;
    }

    .rv-crosshair__title {
      font-weight: bold;
      white-space: nowrap;
    }

    .rv-crosshair__item {
      white-space: nowrap;
    }

    .rv-hint {
      position: absolute;
      pointer-events: none;
    }

    .rv-hint__content {
      border-radius: 4px;
      padding: 7px 10px;
      font-size: 12px;
      background: ${colorScales.fg(0.22).css()};
      box-shadow: 0 2px 4px ${base.fgAlpha};
      color: ${base.bg};
      text-align: left;
      white-space: nowrap;
    }

    .rv-discrete-color-legend {
      box-sizing: border-box;
      overflow-y: auto;
      font-size: 12px;
      position: absolute;
      padding: 1rem;
      top: 1rem;
      right: 1rem;
      border: 1px solid ${colorScales.viz(0.55).css()};
      background: ${chroma(base.bg).alpha(0.8).css()};
    }

    .rv-discrete-color-legend.horizontal {
      white-space: nowrap;
    }

    .rv-discrete-color-legend-item {
      color: ${base.bg};
      border-radius: 1px;
      padding: 9px 10px;
    }

    .rv-discrete-color-legend-item.horizontal {
      display: inline-block;
    }

    .rv-discrete-color-legend-item.horizontal
      .rv-discrete-color-legend-item__title {
      margin-left: 0;
      display: block;
    }

    .rv-discrete-color-legend-item__color {
      background: none;
      display: inline-block;
      height: 4px;
      vertical-align: middle;
      width: 14px;
    }

    .rv-discrete-color-legend-item__title {
      margin-left: 10px;
    }

    .rv-discrete-color-legend-item.disabled {
      color: ${colorScales.fg(0.72).css()};
    }

    .rv-discrete-color-legend-item.clickable {
      cursor: pointer;
    }

    .rv-discrete-color-legend-item.clickable:hover {
      background: ${base.bg};
    }

    .rv-search-wrapper {
      display: flex;
      flex-direction: column;
    }

    .rv-search-wrapper__form {
      flex: 0;
    }

    .rv-search-wrapper__form__input {
      width: 100%;
      color: ${colorScales.fg(0.65).css()};
      border: 1px solid ${colorScales.fg(0.9).css()};
      padding: 7px 10px;
      font-size: 12px;
      box-sizing: border-box;
      border-radius: 2px;
      margin: 0 0 9px;
      outline: 0;
    }

    .rv-search-wrapper__contents {
      flex: 1;
      overflow: auto;
    }

    .rv-continuous-color-legend {
      font-size: 12px;
    }

    .rv-continuous-color-legend .rv-gradient {
      height: 4px;
      border-radius: 2px;
      margin-bottom: 5px;
    }

    .rv-continuous-size-legend {
      font-size: 12px;
    }

    .rv-continuous-size-legend .rv-bubbles {
      text-align: justify;
      overflow: hidden;
      margin-bottom: 5px;
      width: 100%;
    }

    .rv-continuous-size-legend .rv-bubble {
      background: ${colorScales.fg(0.85).css()};
      display: inline-block;
      vertical-align: bottom;
    }

    .rv-continuous-size-legend .rv-spacer {
      display: inline-block;
      font-size: 0;
      line-height: 0;
      width: 100%;
    }

    .rv-legend-titles {
      height: 16px;
      position: relative;
    }

    .rv-legend-titles__left,
    .rv-legend-titles__right,
    .rv-legend-titles__center {
      position: absolute;
      white-space: nowrap;
      overflow: hidden;
    }

    .rv-legend-titles__center {
      display: block;
      text-align: center;
      width: 100%;
    }

    .rv-legend-titles__right {
      right: 0;
    }

    .rv-radial-chart .rv-xy-plot__series--label {
      pointer-events: none;
    }
    .rv-discrete-color-legend-item__title { color: ${base.fg} }
    `,
    themeName,
    XYPlot: {
      margin: 60
    },
    XAxis: {
      orientation: "bottom",
      position: "middle",
      tickSizeInner: 1,
      tickSizeOuter: 5,
      style: { fill: base.fg }
    },
    YAxis: {
      orientation: "left",
      position: "middle",
      tickSizeInner: 1,
      tickSizeOuter: 5,
      style: { fill: base.fg }
    },
    MarkSeries: {
      size: 4
    },
    MarkSeriesStyle: {
      fillOpacity: 0.2,
      strokeWidth: 1
    },
    SVG: {
      filters,
      gradients: {
        graphBackground: ({ id, color }: { id: string; color: string }) => (
          <linearGradient id={id} gradientTransform="rotate(90)">
            <stop offset={0} stopColor={color} stopOpacity={0} />
            <stop offset={1} stopColor={color} stopOpacity={0.2} />
          </linearGradient>
        ),
        warning: ({ id }: { id: string }) => (
          <linearGradient id={id}>
            <stop
              offset={0}
              stopColor={colorScales.warning(0).css()}
              stopOpacity={1}
            />
            <stop
              offset={0.5}
              stopColor={colorScales.warning(0.5).css()}
              stopOpacity={1}
            />
            <stop
              offset={1}
              stopColor={colorScales.warning(1).css()}
              stopOpacity={1}
            />
          </linearGradient>
        )
      },
      patterns: {
        createCrossHatchPattern: ({
          fill = "white",
          fillOpacity = 1,
          id = "crosshatch"
        }: ISVGPatternProps) => (
          <pattern id={id} patternUnits="userSpaceOnUse" width="8" height="8">
            <path
              d="M0 0L8 8ZM8 0L0 8Z"
              strokeWidth="0.5"
              stroke={fill}
              strokeOpacity={fillOpacity}
            />
          </pattern>
        ),
        createStripePattern: ({
          fill = colorScales.primary(1).css(),
          id = "stripes",
          textureOpacity = 0.5,
          fillOpacity = 1
        }: ISVGPatternProps) => (
          <pattern id={id} patternUnits="userSpaceOnUse" width="5" height="5">
            <rect
              width="5"
              height="5"
              x="0"
              y="0"
              fill={fill}
              fillOpacity={fillOpacity}
            />
            <image
              opacity={textureOpacity}
              xlinkHref="data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPSc1JyBoZWlnaHQ9JzUnPgogIDxyZWN0IHdpZHRoPSc1JyBoZWlnaHQ9JzUnIGZpbGw9J3doaXRlJy8+CiAgPHBhdGggZD0nTTAgNUw1IDBaTTYgNEw0IDZaTS0xIDFMMSAtMVonIHN0cm9rZT0nIzg4OCcgc3Ryb2tlLXdpZHRoPScxJy8+Cjwvc3ZnPg=="
              x="0"
              y="0"
              width="5"
              height="5"
            />
          </pattern>
        )
      }
    }
  }
}

const reactVisThemes: TReactVizThemes = {
  light: generateVizTheme({
    base: baseColors.light,
    colorScales: getColorScales("light"),
    themeName: "Gamma Light"
  }),
  fabriq: generateVizTheme({
    base: baseColors.fabriq,
    colorScales: getColorScales("fabriq"),
    themeName: "Fabriq"
  }),
  dark: generateVizTheme({
    base: baseColors.dark,
    colorScales: getColorScales("dark"),
    themeName: "Gamma Dark"
  })
}

function lightDark(options: any[], mode: string) {
  return mode === "light" ? options[0] : options[1]
}

const getElevationStyles = (key: string) => {
  switch (key) {
    case "dark":
      return {
        boxShadow: `rgb(0 0 0 / 7%) 0px 2px 6px`
      }
    default:
      // light
      return {
        boxShadow: `rgb(0 0 0 / 7%) 0px 2px 6px`
      }
  }
}

type TTypographyDef = {
  [key: string]: string | { [key: string]: string }
}

export function generateUITheme({
  base,
  colorScales,
  themeName,
  series,
  seriesBCG,
  seriesPaletteKey,
  mode,
  viz,
  baseTheme = "light",
  typography = typographyDefault,
  fontFaces = fontFacesDefault
}: {
  base: TBasePalette
  baseTheme?: "light" | "dark"
  colorScales: TColorScales
  themeName: string
  series: string[]
  seriesBCG: string[]
  categoryColors: TCategoryColors
  seriesPaletteKey: "light" | "dark" | "main"
  mode: PaletteMode
  viz: TReactVizOverrides
  typography: any
  fontFaces: any[]
}): Theme {
  return createTheme({
    seriesPaletteKey,
    colorScales,
    viz,
    baseTheme,
    spacing: 8,
    components: {
      MuiAppBar: {
        defaultProps: {
          color: "default"
        }
      },
      MuiCssBaseline: {
        styleOverrides: {
          "@font-face": fontFaces
        }
      },
      // @ts-ignore
      MuiAlert: {
        styleOverrides: {
          standardError: {
            color: base.fg,
            backgroundColor: colorScales.reds(1).alpha(0.2).css()
          },
          standardInfo: {
            color: base.fg,
            backgroundColor: colorScales.blues(1).alpha(0.2).css()
          },
          standardWarning: {
            color: base.fg,
            backgroundColor: "rgba(255, 152, 0, 0.2)"
          },
          standardSuccess: {
            color: base.fg,
            backgroundColor: colorScales.greens(1).alpha(0.2).css()
          }
        }
      },
      MuiListSubheader: {
        styleOverrides: {
          sticky: {
            backgroundColor: "transparent",
            borderBottom: `1px solid ${chroma(base.fg).alpha(0.3).css()}`
          }
        }
      },
      MuiSlider: {
        defaultProps: {
          color: "secondary"
        },
        styleOverrides: {
          thumb: {
            borderRadius: "50%",
            border: `1px solid ${base.fgAlpha}`
          },
          mark: {
            opacity: 0.2
          },
          markActive: {
            opacity: 0.3
          }
        }
      },
      MuiSwitch: {
        defaultProps: {
          color: "primary"
        },
        styleOverrides: {
          root: {
            padding: 8,
            width: 62
          },
          sizeSmall: {
            padding: 4,
            width: 40
          },
          switchBase: {
            padding: 6,
            borderRadius: 6,
            "&.Mui-disabled": {
              cursor: "not-allowed"
            },
            "&.Mui-checked": {
              transform: "translateX(24px)"
            }
          },
          thumb: {
            width: 26,
            height: 26,
            borderRadius: 9999,
            transform: "scale(0.5)",
            border: `1px solid rgb(0, 0, 0)`,
            background: `white url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEgAAABICAYAAABV7bNHAAAA2ElEQVR4Ae3aMQ6DMBAAQTr+xmPp8rd0xAXtlYEtZiXXWCMlAvs2SZIkSZIkTR1rnWvt23vt9x6OIs53rWutz0tI+/3s697LUcS5nkQacK4M0oDzDNKAU0M6h439EWnAmdf5JtC8wRkp/ew+UuiZfaQQTh8phFNFSuJUkLo4AaQuTgCpixNA6uIEkLo4AaQ2TgCpjRNAyuIEkODMSHBGJDiA/MT8SQdwIHlR9KnhY9VxhwMzR66dHNq79nFx6Oo5kOEF4y8GqIzgPZAhTmPABsklSZIkSZL0AyfhWecRaKXrAAAAAElFTkSuQmCC) center center no-repeat`,
            backgroundSize: "100% 100%",
            opacity: 1,
            boxShadow:
              "inset 0 0 10px rgba(255, 255, 255, 0.6), 0 0 5px rgba(0, 0, 0, 0.4)",
            ".MuiSwitch-sizeSmall &": {
              transform: "scale(0.6)"
            },
            ".Mui-checked > &": {
              opacity: 1,
              border: `1px solid rgb(255, 255, 255)`,
              background: `white url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAkCAQAAABLCVATAAAAb0lEQVRIx2NgGAWjYCABC0M4dYxZy/CfoZ46xoCgA3WMqR81ZjgZE8iwEqiFCsb8Aipdi2EUyZ6qhSpHNYqssKnHMIrsIEY1iqKYQhjFSWmEw4x6QXm6gRn1n/Iyp546xsCMooIxIODAMApGAXEAAG6vZX8ePL3YAAAAAElFTkSuQmCC) center center no-repeat`,
              backgroundSize: "100% 100%"
            },
            ".Mui-disabled > &": {
              border: `1px solid ${chroma(base.fg).alpha(0.2).css()}`,
              opacity: 0.3
            }
          },
          track: {
            borderRadius: 9999,
            boxShadow: "inset 0 0 3px rgba(0, 0, 0, 0.5)",
            border: `1px solid ${chroma(base.fg).alpha(0.3).css()}`
          }
        }
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: colorScales.bg(0.1).css(),
            borderRadius: "3px",
            boxShadow: `inset 0 0 10px ${chroma(colorScales.bg(1))
              .alpha(0.2)
              .css()}`
          },
          underline: {
            "&:before": {
              borderBottomColor: colorScales.primary(0).css()
            }
          }
        }
      },
      MuiAvatar: {
        styleOverrides: {
          colorDefault: {
            backgroundColor: "#565656"
          }
        }
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            justifyContent: "flex-end",
            padding: "0.75rem 1rem",
            borderTop: `1px solid ${base.border}`,
            background: base.bgContent
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 3,
            backgroundColor: colorScales.bg(0.4).css(),
            border: `1px solid ${chroma(base.fg).alpha(0.3).css()}`,
            boxShadow: `0 2px 9px ${chroma(base.fg).alpha(0.02).css()}`
          },
          label: {
            textTransform: "uppercase",
            fontSize: "0.6875rem",
            fontWeight: 400
          },
          colorPrimary: {
            backgroundColor: colorScales.primary(0.5).css(),
            color: "white"
          },
          colorSecondary: {
            backgroundColor: colorScales.secondary(0.5).css(),
            color: "white"
          },
          avatar: {
            "$-outlined &": {
              marginLeft: 0
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.MuiInputLabel-filled": {
              left: "0.25rem"
            }
          }
        }
      },
      MuiSelect: {
        defaultProps: {
          variant: "outlined",
          label: false
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            "& > .MuiSelect-outlined, &.MuiFilledInput-root": {
              backgroundColor: `${lightDark(
                ["white", chroma(base.fg).alpha(0.05).css()],
                mode
              )}`,
              borderRadius: 3,
              border: `1px solid ${base.border}`,
              borderWidth: 1,
              boxShadow: `${lightDark(
                ["none", `inset 0 0 10px ${chroma(base.bg).alpha(0.5).css()}`],
                mode
              )}`
            },
            "& > .MuiSelect-outlined, & > .MuiNativeSelect-select": {
              padding: "0.75rem 1rem"
            },
            "&.MuiFilledInput-root": {
              "&.Mui-focused": {
                backgroundColor: `${lightDark(
                  ["white", chroma(base.fg).alpha(0.05).css()],
                  mode
                )}`
              },
              "& > .MuiFilledInput-input": {
                padding: "0.75rem 1rem"
              },
              "&::before": {
                display: "none"
              },
              "&::after": {
                display: "none"
              }
            },
            "&.MuiNativeSelect-root": {
              "&::before": {
                display: "none"
              },
              "&::after": {
                display: "none"
              },
              "~ .MuiInputLabel-animated": {
                // display: "none"
              }
            },
            "& > .MuiOutlinedInput-notchedOutline": {
              display: "none"
            }
          },
          input: {
            "&:focus": {
              borderRadius: 4,
              borderColor: "#80bdff",
              boxShadow: "0 0 0 0.2rem rgba(0,123,255,.25)"
            }
          }
        }
      },
      MuiNativeSelect: {
        defaultProps: {
          variant: "outlined"
        },
        styleOverrides: {
          select: {
            backgroundColor: `${lightDark(
              ["white", chroma(base.fg).alpha(0.05).css()],
              mode
            )}`,
            borderRadius: 3,
            border: `1px solid ${base.border}`,
            borderWidth: 1,
            boxShadow: `${lightDark(
              ["none", `inset 0 0 10px ${chroma(base.bg).alpha(0.5).css()}`],
              mode
            )}`
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 0
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            background: base.bg,
            // border: `1px solid ${chroma(base.fg).alpha(0.15).css()}`,
            border: `1px solid ${base.border}`,
            borderRadius: 6
          },
          elevation1: getElevationStyles(mode)
        }
      },
      // MuiExpansionPanelDetails: {
      //   styleOverrides: {
      //     root: {
      //       backgroundImage: `linear-gradient(180deg, ${
      //         baseColors.bg
      //       } 0%, ${colorScales.bg(0.08).css()} 20px)`,
      //       boxShadow: `inset 0 0 5px ${chroma(base.bg).alpha(0.9).css()}`,
      //       padding: 24
      //     }
      //   }
      // },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            backgroundImage: "none"
          }
        }
      },
      MuiDialog: {
        defaultProps: {
          PaperComponent: themeName === "Fabriq" ? MeshPaper : Paper,
          PaperProps: themeName === "Fabriq" ? {
            elevation: 0
          } : {}
        },
        styleOverrides: themeName === "Fabriq" ? {
          paper: {
            padding: "2rem"
            // todo - alpha mesh border
          }
        } : {}
      },
      MuiButton: {
        defaultProps: {
          variant: "outlined"
        },
        styleOverrides: {
          root: {
            borderRadius: 9999,
            "&$disabled": {
              opacity: 0.8
            }
          },
          contained: {
            background: base.bg,
            boxShadow: `0 0 5px ${chroma(base.fg).alpha(0.3).css()}`,
            "&:hover": {
              boxShadow: `0 0 5px ${chroma(base.fg).alpha(0.5).css()}`
            },
            "&$disabled": {
              background: `linear-gradient(180deg, white 0%, rgb(200, 200, 200) 100%)`,
              color: "rgb(111, 111, 111)"
            }
          },
          containedPrimary: {
            boxShadow: `0 0 1px ${chroma(base.fg).alpha(0.1).css()}`,
            color: "white",
            // background: `linear-gradient(0deg, ${colorScales
            //   .primary(0.3)
            //   .css()} 0%, ${colorScales.primary(0.5).css()} 100%)`,
            // "&$disabled": {
            //   background: `linear-gradient(0deg, ${colorScales
            //     .primary(0.3)
            //     .darken(2)
            //     .css()} 0%, ${colorScales.primary(0.5).darken(2).css()} 100%)`
            // },
            background: colorScales.primary(0.5).css(),
            "&$disabled": {
              background: colorScales.primary(0.5).darken(2).css()
            },

            "&:hover": {
              boxShadow: `0 0 5px ${chroma(base.fg).alpha(0.3).css()}`
            }
          },
          containedSecondary: {
            boxShadow: `0 0 1px ${chroma(base.fg).alpha(0.1).css()}`,
            color: "white",
            // background: `linear-gradient(0deg, ${colorScales
            //   .secondary(0)
            //   .css()} 0%, ${colorScales.secondary(0.5).css()} 100%)`,
            // "&$disabled": {
            //   background: `linear-gradient(0deg, ${colorScales
            //     .secondary(0)
            //     .darken(3)
            //     .css()} 0%, ${colorScales.secondary(0.5).darken(3).css()} 100%)`

            // },
            background: colorScales.secondary(0.5).css(),
            "&$disabled": {
              background: colorScales.secondary(0.5).darken(3).css()
            },
            "&:hover": {
              boxShadow: `0 0 5px ${chroma(base.fg).alpha(0.3).css()}`
            }
          },
          outlined: {
            background: base.bg,
            "&:hover": {
              boxShadow: `0 0 5px ${chroma(base.fg).alpha(0.3).css()}`,
              background: colorScales.fg(0.5).alpha(0.1).css()
            }
          },
          outlinedPrimary: {
            // borderImage: `linear-gradient(
            //   180deg,
            //   ${colorScales.primary(0).css()} 0%,
            //   ${colorScales.primary(1).css()} 50%,
            //   ${colorScales.primary(0).css()} 100%
            // )`,
            background: base.bg,
            borderColor: colorScales.primary(0.5).css(),
            color: colorScales.primary(0.5).css(),
            "&:hover": {
              boxShadow: `0 0 5px ${chroma(base.fg).alpha(0.3).css()}`,
              background: colorScales.primary(0.5).alpha(0.1).css()
            }

            // borderImageSlice: 1,
            // "&$disabled": {
            //   borderColor: colorScales.primary(0).darken(0.4).css()
            // }
          },
          outlinedSecondary: {
            // borderImage: `linear-gradient(
            //   180deg,
            //   ${colorScales.secondary(0).css()} 0%,
            //   ${colorScales.secondary(1).css()} 50%,
            //   ${colorScales.secondary(0).css()} 100%
            // )`,
            background: base.bg,
            borderColor: colorScales.secondary(0.5).css(),
            color: colorScales.secondary(0.5).css(),
            "&:hover": {
              boxShadow: `0 0 5px ${chroma(base.fg).alpha(0.3).css()}`,
              background: colorScales.secondary(0.5).alpha(0.1).css()
            }
            // borderImageSlice: 1,
            // "&$disabled": {
            //   borderColor: colorScales.secondary(0).darken(0.4).css()
            // }
          }
        }
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            background: "none",
            "& > .MuiInputLabel-root": {
              display: "none"
            }
          }
        }
      },
      MuiTextField: {
        defaultProps: {
          variant: "filled"
        },
        styleOverrides: {}
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            "&$selected": {
              boxShadow: `inset 0 0 4px ${chroma(colorScales.bg(0))
                .alpha(0.1)
                .css()}`
            }
          }
        }
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            "&$selected": {
              backgroundColor: `${chroma(colorScales.fg(0)).alpha(0.05).css()}`
            }
          }
        }
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 32
          }
        }
      }
    },
    shape: {
      borderRadius: 3
    },
    categoryColors,
    series,
    seriesBCG,
    seriesQ: chroma.scale("PuRd").colors(3),
    themeName,
    basePalette: base,
    palette: {
      background: {
        default: colorScales.bg(0.1).css(),
        paper: colorScales.bg(0.22).css()
      },
      divider: chroma(base.fg).alpha(0.2).css(),
      error: {
        contrastText: base.fg,
        dark: colorScales.reds(0).css(),
        light: colorScales.reds(1).brighten(1).css(),
        main: colorScales.reds(1).css()
      },
      info: {
        light: colorScales.blues(1).css(),
        main: colorScales.blues(0.5).css(),
        dark: colorScales.blues(0).css(),
        contrastText: base.fg
      },
      mode,
      text: {
        primary: base.fg
      },
      primary: {
        main: colorScales.primary(0.5).css(),
        dark: colorScales.primary(0).css(),
        light: colorScales.primary(1).css(),
        contrastText: base.fg
      },
      secondary: {
        main: colorScales.secondary(0.5).css(),
        dark: colorScales.secondary(0).css(),
        light: colorScales.secondary(1).css(),
        contrastText: base.fg
      },
      // spice: {
      //   main: colorScales.spice(0.5).css(),
      //   dark: colorScales.spice(0).css(),
      //   light: colorScales.spice(1).css(),
      //   contrastText: base.bg
      // },
      success: {
        main: colorScales.greens(0.5).css(),
        dark: colorScales.greens(0).css(),
        light: colorScales.greens(1).css(),
        contrastText: base.fg
      },
      warning: {
        light: "rgb(255, 183, 77)",
        main: "rgb(255, 152, 0)",
        dark: "rgb(143, 71, 0)",
        contrastText: chroma(base.bg).alpha(0.87).css()
      }
    },
    typography
  })
}

interface ICustomThemeProps {
  base: any
  themeName: string
  primary: string[]
  secondary: string[]
  mode: PaletteMode
  typography: any
  fontFaces: any[]
  series: string[]
  // overrides: any
}

export const createCustomTheme = ({
  base,
  themeName = "Custom Theme",
  primary = [],
  secondary = [],
  mode = "dark",
  typography = typographyDefault,
  fontFaces = fontFacesDefault,
  series = longChartSeries
}: // overrides = {}
ICustomThemeProps) => {
  const baseColorScales = getColorScales(mode)
  if (!baseColorScales) {
    console.warn("Please specify mode = light | dark")
    return null
  }
  if (!Array.isArray(primary)) {
    console.warn(
      "Please specify primary prop as an array [darkColor, mainColor, lightColor]"
    )
    return null
  }
  if (!Array.isArray(secondary)) {
    console.warn(
      "Please specify secondary prop as an array [darkColor, mainColor, lightColor]"
    )
    return null
  }

  // if only a single color is provided, create a scale with a darkened version of it
  const primaryColorsArr =
    primary.length > 1
      ? primary
      : [chroma(primary[0]).darken(1).css(), ...primary]
  baseColorScales.primary = chroma.scale(primaryColorsArr)

  const secondaryColorsArr =
    secondary.length > 1
      ? secondary
      : [chroma(secondary[0]).darken(1).css(), ...secondary]
  baseColorScales.secondary = chroma.scale(secondaryColorsArr)

  // const validTypographyKeys = Object.keys(typographyDefault)
  // const validTypography = {}
  // for (const key in typography) {
  //   if (validTypographyKeys.includes(key)) {
  //     validTypography[key] = typography[key]
  //   } else {
  //     console.warn(
  //       "Invalid typography key",
  //       key,
  //       "must be one of",
  //       validTypographyKeys
  //     )
  //   }
  // }

  const theme = generateUITheme({
    base: base || baseColors[mode],
    themeName,
    baseTheme: "light",
    mode,
    seriesPaletteKey: "main", // which color variant to use on charting components (light on light bg won't have good contrast)
    colorScales: baseColorScales,
    viz: reactVisThemes.light,
    seriesBCG: bcgPalette,
    series,
    categoryColors,
    typography:
      typography && Object.keys(typography).length
        ? typography
        : typographyDefault,
    fontFaces:
      Array.isArray(fontFaces) && fontFaces.length
        ? fontFaces
        : fontFacesDefault
  })

  // if (overrides && Object.keys(overrides).length) {
  //   for (const key in overrides) {
  //     if (key && theme.overrides) {
  //       theme.overrides[key] = overrides[key]
  //     }
  //   }
  // }

  console.log("Generated custom theme", theme)
  return theme
}

export const themes: { light: Theme; fabriq: Theme; dark: Theme } = {
  light: generateUITheme({
    base: baseColors.light,
    baseTheme: "light",
    themeName: "Gamma Light",
    mode: "light",
    seriesPaletteKey: "main", // which color variant to use on charting components (light on light bg won't have good contrast)
    colorScales: getColorScales("light"),
    viz: reactVisThemes.light,
    seriesBCG: bcgPalette,
    categoryColors,
    series: longChartSeries,
    typography: typographyDefault,
    fontFaces: fontFacesDefault
  }),
  fabriq: generateUITheme({
    base: baseColors.fabriq,
    baseTheme: "light",
    themeName: "Fabriq",
    mode: "light",
    seriesPaletteKey: "main", // which color variant to use on charting components (light on light bg won't have good contrast)
    colorScales: getColorScales("fabriq"),
    viz: reactVisThemes.fabriq,
    seriesBCG: bcgPalette,
    series: longChartSeries,
    categoryColors,
    typography: typographyDefault,
    fontFaces: fontFacesDefault
  }),
  dark: generateUITheme({
    base: baseColors.dark,
    baseTheme: "dark",
    themeName: "Gamma Dark",
    mode: "dark",
    seriesPaletteKey: "light",
    colorScales: getColorScales("dark"),
    viz: reactVisThemes.dark,
    seriesBCG: bcgPalette,
    series: longChartSeries,
    categoryColors,
    typography: typographyDefault,
    fontFaces: fontFacesDefault
  })
  // spice: createCustomTheme({
  //   mode: "dark",
  //   primary: ["#ff00CC"],
  //   secondary: ["#00CCff"]
  // })
}
